// src/pages/po/RequisicaoItemsSidePanelContent.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faCheck, faFileInvoiceDollar } from '@/icons';
import axios from 'axios';
import { apiGet, apiPost, apiPut } from '../../services/api';
import ScrollableTable from '../../components/common/ScrollableTable';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import ReturnButton from '../../components/common/ReturnButton';
import RejectButton from '../../components/common/RejectButton';
import SaveButton from '../../components/common/SaveButton';
import GiveUpButton from '../../components/common/GiveUpButton';
import Modal from '../../components/common/Modal';
import HistoricoItemModalContent from './HistoricoItemModalContent';
import CompraHistoricoModalContent from './CompraHistoricoModalContent';
import { RequisicaoItem, RequisicaoHeader } from '../../types/compras/Requisicao';
import { HistoricoResponse, CompraHistoricoResponse } from '../../types/compras/Historico';
import { ReverterAprovacaoItemResponse } from '../../types/aprovacao/Aprovacao';
import './RequisicaoItemsSidePanelContent.css';
import { formatDateTime , formatDate , formatDateOrDash , getEmptyDetailsMessage} from '../../utils/formatUtils';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { useApiCall } from '../../hooks/useApiCall';
import { useOperacaoLote } from '@/hooks/useOperacaoLote';
import { chaveLote, useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import {
  contadoresInline,
  contarSelecionados,
  fragmentoSelectAll,
  mensagemResultado,
  textoSelecao,
  type ModoSelecao,
} from '@/utils/selectAll';
import { useAuth } from '../../contexts/AuthContext';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

// Modos de operacao do side panel:
//   view       — somente leitura (default).
//   devolver   — fluxo legado de devolucao (so para tipoRequisicao=COMPRA).
//   desaprovar — desfaz aprovacao (item PENDENTE → APROVAR). Coluna Pendentes.
//   revogar    — desfaz reprovacao (item REPROVADO → APROVAR). Coluna Finalizados.
type Modo = 'view' | 'devolver' | 'desaprovar' | 'revogar';

interface RequisicaoItemsSidePanelContentProps {
  requisicao: { id: number };
  theme: 'dark' | 'light';
  onClose: () => void;
  onDataChange?: () => void; // Nova prop opcional
  itemFilter?: string | null;
  tipoRequisicao?: string | null;
  // Quando true, exibe na coluna de acoes o icone que abre o modal de
  // Historico de Compras do produto (rota /v1/historico-compras-produto).
  // Ativado apenas em /compras (kanban Requisicoes Pendentes).
  showPurchaseHistoryAction?: boolean;
}

const formatBRL = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Teto do motivo do lote na devolucao — o mesmo @Size do
// DevolverItensRequisicaoRequestDTO. Nao confundir com os 25 caracteres do
// motivo POR ITEM do modo enumerado (`reason-select`), que e outro campo.
const DESCRICAO_LOTE_MAX = 100;

// Status que o select-all da devolucao envia em `filtro.status`.
//
// Existe para reproduzir o WHERE da LISTAGEM desta tela, que filtra
// `ri.status != 'APROVAR'` de forma fixa no SQL
// (OrdemCompraItemRepository:216). O resolver da devolucao NAO exclui APROVAR
// sozinho, e `status` vazio nao filtra nada — sem esta lista, "selecionar
// tudo" alcancaria item aguardando alcada, que nao esta na tela e que o
// usuario nao pediu para devolver.
//
// A lista e NEGATIVA por necessidade: o contrato so aceita whitelist, entao
// "todos menos APROVAR" tem de ser enumerado. FINALIZADO tambem fica de fora
// porque a tela o remove (fetchItems) — e, de todo modo, item finalizado tem
// saldo zero e o resolver ja exige saldo > 0.
//
// Se o backend criar um status novo, o efeito aqui e CONSERVADOR: o item nao
// e alcancado pelo select-all, em vez de ser devolvido indevidamente.
const STATUS_DEVOLVIVEIS = [
  'ABERTA',
  'PENDENTE',
  'CANCELADO',
  'RASCUNHO',
  'CONFIRMADO',
  'PARCIAL',
  'CONFERENCIA',
  'REPROVADO',
];

const RequisicaoItemsSidePanelContent: React.FC<RequisicaoItemsSidePanelContentProps> = ({
  requisicao,
  theme,
  onClose,
  onDataChange,
  itemFilter,
  tipoRequisicao,
  showPurchaseHistoryAction = false,
}) => {
  const { hasPermission } = useAuth();
  const podeAprovar = hasPermission('requisicoes.aprovar_requisicao_compra');
  const [header, setHeader] = useState<RequisicaoHeader | null>(null);
  const { callApi, isLoading: isSaving } = useApiCall();
  const [historicoData, setHistoricoData] = useState<HistoricoResponse | null>(null);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [compraHistoricoData, setCompraHistoricoData] = useState<CompraHistoricoResponse | null>(null);
  const [compraHistoricoModalOpen, setCompraHistoricoModalOpen] = useState(false);
  // O modo so sai de 'view' quando o user clica em Devolver/Reprovar/Aprovar,
  // e e o modo que decide se a coluna de checkbox existe: no 'view' ela NAO
  // existe, porque so os modos de acao carregam o select-all de verdade.
  const [modo, setModo] = useState<Modo>('view');
  const [selectedItems, setSelectedItems] = useState<RequisicaoItem[]>([]);
  const [returnQuantities, setReturnQuantities] = useState<{ [key: number]: number }>({});
  const [returnReasons, setReturnReasons] = useState<{ [key: number]: string }>({});
  const [finalizedItems, setFinalizedItems] = useState<number[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  // --- "Selecionar tudo entre paginas" (issue #31, superficies 2 e 7) --------
  // Cobre os modos de reversao (superficie 2) e o modo 'devolver' (superficie
  // 7). O modo 'view' fica de fora, e por isso a coluna de checkbox tambem fica
  // — os dois nascem e morrem juntos. Ate a issue #33 nao era assim: em /compras
  // a coluna aparecia no 'view' sem esta prop, entao o cabecalho caia no caminho
  // historico do ScrollableTable (marca so o carregado, pinta CHEIO, e se
  // desmarca sozinho ao rolar), e a selecao parcial atravessava para o Devolver.
  //
  // Os estados de selecao sao COMPARTILHADOS entre os modos de proposito: `modo`
  // e um so, os fluxos sao mutuamente exclusivos, e clearModeState() zera tudo
  // na troca. Dois pares de estado paralelos divergiriam no primeiro descuido.
  const ehModoReversao = modo === 'desaprovar' || modo === 'revogar';
  const ehModoDevolver = modo === 'devolver';
  const suportaSelectAll = ehModoReversao || ehModoDevolver;
  const [modoSelecao, setModoSelecao] = useState<ModoSelecao>('enumerado');
  const [excludeIds, setExcludeIds] = useState<Set<number>>(new Set());
  // Motivo unico do lote na devolucao (superficie 7). Obrigatorio quando
  // selectAll=true: ele viaja ate o template do e-mail que o requisitante
  // recebe, entao nao existe default vazio.
  const [descricaoLote, setDescricaoLote] = useState('');
  const { disparar, estaProcessando } = useOperacaoLote();
  const { aoConcluir } = useOperacoesLote();

  // ATENCAO — `requisicao.id` e o ID DA REQUISICAO nos DOIS mounts. O que muda
  // e a rota de leitura: com tipoRequisicao='COMPRA' (mount de /compras) o
  // fetch vai para /v1/ordens-compra/{id}/itens/tipo-compra-requisicao, cujo
  // path SUGERE ordem de compra mas cujo {id} o backend resolve como requisicao
  // (OrdemCompraItemService:114 faz requisicaoRepository.existsById(id), e o SQL
  // e `FROM po.requisicao_item ri WHERE ri.requisicao_id = :requisicaoId`).
  //
  // Consequencia para o motor de lote: os dois mounts falam da MESMA requisicao,
  // com acoes diferentes. Sem o guard abaixo, um lote REVERTER:186 disparado em
  // /requisicoes acordava tambem o painel de /compras da requisicao 186 e
  // apagava o que o usuario tinha digitado no modo devolver. Nao e coincidencia
  // de ids — e o mesmo pai visto por duas telas.
  //
  // A reversao so existe no mount de /requisicoes; a devolucao, so no de
  // /compras. Cada flag e constante dentro do seu mount.
  const suportaReversao = !tipoRequisicao;
  const suportaDevolucao = tipoRequisicao === 'COMPRA';

  // Espelham a trava (acao, chave_pai) do backend. REVERTER cobre Desaprovar E
  // Revogar (abas do mesmo endpoint, separadas por filtro.itens); DEVOLVER e a
  // devolucao ao requisitante. Sao acoes distintas: o backend nao serializa uma
  // contra a outra, e o que protege os itens e o SELECT ... FOR UPDATE.
  const chaveTravaReversao = chaveLote('REVERTER', requisicao.id);
  const chaveTravaDevolucao = chaveLote('DEVOLVER', requisicao.id);
  const processandoLote = ehModoDevolver
    ? estaProcessando(chaveTravaDevolucao)
    : suportaReversao && estaProcessando(chaveTravaReversao);

  const fetchItems = useCallback(async (pageNumber: number, _currentItems: RequisicaoItem[]): Promise<{ items: RequisicaoItem[], totalItems: number }> => {
    const queryParams = new URLSearchParams({
      page: pageNumber.toString(),
      size: '10',
    });
    let endpoint = `/v1/requisicoes/${requisicao.id}/itens`;
    if (tipoRequisicao) {
      endpoint = `/v1/ordens-compra/${requisicao.id}/itens/tipo-compra-requisicao`;
      queryParams.append('tipoRequisicao', tipoRequisicao);
    } else if (itemFilter) {
      queryParams.append('itens', itemFilter);
    }
    const res = await apiGet(`${endpoint}?${queryParams.toString()}`);
    const { dataCriacao, nroContato, status, origem, codFilial, usuarioId, nomeFantasia, nomeUsuario, nomeCliente, codCliente, codConsultor, nomeConsultor, dataEncerramento, itens } = res.data;

    if (pageNumber === 0) {
      setHeader({ dataCriacao, nroContato, status, origem, codFilial, usuarioId, nomeFantasia, nomeUsuario, nomeCliente, codCliente, codConsultor, nomeConsultor, dataEncerramento, });
      setTotalItems(itens.page.totalElements);
    }

    const filteredItems = itemFilter === 'finalizado'
      ? itens.content
      : itens.content.filter((item: RequisicaoItem) => item.status !== 'FINALIZADO');

    if (pageNumber === 0) {
      const initialFinalizedItems = filteredItems
        .filter((item: RequisicaoItem) => item.status === 'FINALIZADO')
        .map((item: RequisicaoItem) => item.id);
      setFinalizedItems(initialFinalizedItems);
    } else {
      const newFinalizedItems = filteredItems
        .filter((item: RequisicaoItem) => item.status === 'FINALIZADO')
        .map((item: RequisicaoItem) => item.id);
      setFinalizedItems((prev) => [...new Set([...prev, ...newFinalizedItems])]);
    }

    return { items: filteredItems, totalItems: itens.page.totalElements };
  }, [requisicao.id, itemFilter, tipoRequisicao]);

  const { items, hasMore, isLoading, loadMore, reset, updateItems } = usePaginatedFetch<RequisicaoItem>({
    fetchFunction: fetchItems,
    pageSize: 10,
    uniqueKey: 'id',
    initialPage: 0,
  });

  useEffect(() => {
    buttonRefs.current = items.map((_, i) => buttonRefs.current[i] || null);
  }, [items]);

  useEffect(() => {
    setFinalizedItems([]);
    reset();
  }, [requisicao.id, itemFilter, tipoRequisicao, reset]);

const handleHistoricoClick = async (item: RequisicaoItem, _index: number) => {
  try {
    // Adiciona parâmetros obrigatórios codProduto e codCategoriaProduto, e itemModuloId como opcional
    const queryParams = new URLSearchParams({
      codProduto: item.codProduto,
      codCategoriaProduto: item.codCategoriaProduto.toString(),
      itemModuloId: item.id.toString(),
      page: '0',
      size: '10',
    });
    const res = await apiGet(`/v1/historico-itens?${queryParams.toString()}`);
    logger.log('Resposta da API ao carregar histórico:', res.data);
    setHistoricoData(res.data);
    setSelectedItemId(item.id);
    setHistoricoModalOpen(true);
  } catch (err) {
    logger.error('Erro ao carregar histórico do item:', err);
    // Trata erro 400 com mensagem específica do backend
    if (axios.isAxiosError(err) && err.response?.status === 400 && err.response?.data?.error) {
      await showAlert(err.response.data.error, { variant: 'error' });
    } else {
      await showAlert('Erro ao carregar o histórico. Tente novamente.', { variant: 'error' });
    }
  }
};

  const handleCompraHistoricoClick = async (item: RequisicaoItem) => {
    try {
      if (!item.codProduto || !item.codCategoriaProduto) {
        await showAlert('Dados do item incompletos. Verifique o produto e a categoria.', { variant: 'warning' });
        return;
      }
      const queryParams = new URLSearchParams({
        codProduto: item.codProduto,
        codCategoriaProduto: item.codCategoriaProduto.toString(),
        page: '0',
        size: '10',
      });
      const res = await apiGet(`/v1/historico-compras-produto?${queryParams.toString()}`);
      setCompraHistoricoData(res.data);
      setCompraHistoricoModalOpen(true);
    } catch (err) {
      logger.error('Erro ao carregar histórico de compras do produto:', err);
      if (axios.isAxiosError<{ error?: string }>(err) && err.response?.status === 400 && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao carregar o histórico de compras. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleCloseCompraHistoricoModal = () => {
    setCompraHistoricoModalOpen(false);
    setCompraHistoricoData(null);
  };

  const handleCloseHistoricoModal = () => {
    setHistoricoModalOpen(false);
    setHistoricoData(null);
    setSelectedItemId(null);
  };

  const clearModeState = () => {
    setSelectedItems([]);
    setReturnQuantities({});
    setReturnReasons({});
    setSelectionResetKey((k) => k + 1);
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setDescricaoLote('');
  };

  // Predicado UNICO de selecionabilidade. A mesma funcao alimenta o checkbox da
  // tabela e a contagem exibida no rodape — duas copias divergiriam, que foi a
  // origem dos tres defeitos da superficie 1.
  //
  // Os dois ramos de reversao espelham o WHERE do resolver do backend
  // (RequisicaoItemRepository, ramos 'pendente' e 'finalizado'), alinhados na
  // api-blueltSystem#73. A paridade e estrutural: o qtdAprovada lido aqui e o
  // agregado que a propria listagem projeta, e o resolver faz EXISTS sobre essa
  // mesma fonte. Mexer num lado sem o outro reabre o defeito da #73.
  const podeSelecionar = (item: RequisicaoItem): boolean => {
    if (modo === 'desaprovar') {
      return item.status === 'PENDENTE' && (item.qtdAprovada ?? 0) > 0;
    }
    if (modo === 'revogar') {
      return (
        (item.status === 'REPROVADO' || item.status === 'FINALIZADO') &&
        (item.qtdReprovada ?? 0) > 0
      );
    }
    // modo === 'devolver' (fluxo legado): finalizados ficam disabled.
    // itemFilter='finalizado' mantem libera para compat com legado.
    return itemFilter === 'finalizado' || !finalizedItems.includes(item.id);
  };

  // --- Selecao: convencao de checkbox de cabecalho de tabela ------------------
  //   vazio   -> clicar marca a fila inteira (modo 'todos');
  //   traco   -> parte selecionada; clicar limpa;
  //   marcado -> tudo selecionado; clicar limpa.

  const limparSelecao = () => {
    setModoSelecao('enumerado');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  // Promover para 'todos' no modo devolver ESCONDE a coluna Motivo, e os textos
  // digitados por item nao tem como viajar no payload do selectAll (o override
  // exige a quantidade junto). Deixar isso acontecer em silencio foi o defeito 3
  // da superficie 1 — o motivo sumia da tela E do envio ao marcar a ultima
  // linha. Mesma regra que o dev aprovou la, aplicada aqui:
  //   um unico texto distinto  -> vira o motivo do lote;
  //   varios textos distintos  -> aviso, e o campo do lote fica em branco para
  //                               o usuario escolher um.
  const promoverMotivosParaLote = () => {
    const distintos = [
      ...new Set(
        items
          .map((it) => returnReasons[it.id]?.trim())
          .filter((t): t is string => !!t),
      ),
    ];
    if (distintos.length === 0) return;
    if (distintos.length === 1) {
      setDescricaoLote(distintos[0].slice(0, DESCRICAO_LOTE_MAX));
      return;
    }
    setDescricaoLote('');
    void showAlert('Para devolver todos os itens um único motivo deve ser inserido.', {
      variant: 'warning',
    });
  };

  const entrarEmTodos = () => {
    if (ehModoDevolver) promoverMotivosParaLote();
    setModoSelecao('todos');
    setExcludeIds(new Set());
    setSelectedItems([]);
  };

  const handleSelecionarItem = (item: RequisicaoItem, marcado: boolean) => {
    if (modoSelecao === 'todos') {
      // Desmarcar ALIMENTA a lista de exclusao — e o que permite dizer "todos
      // menos este" sem o navegador conhecer todos.
      setExcludeIds((prev) => {
        const next = new Set(prev);
        if (marcado) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
      return;
    }

    const proximos = marcado
      ? [...selectedItems, item]
      : selectedItems.filter((it) => it.id !== item.id);

    // Marcar a mao todas as linhas selecionaveis equivale a marcar o cabecalho,
    // e o cabecalho significa a fila inteira. A faixa do rodape diz isso.
    const selecionaveis = items.filter(podeSelecionar);
    if (marcado && selecionaveis.length > 0 && proximos.length === selecionaveis.length) {
      entrarEmTodos();
      return;
    }
    setSelectedItems(proximos);
  };

  // Itens carregados que a acao NAO alcanca. Descontados da contagem exibida:
  // sem isso, uma requisicao de 5 itens com 1 revertivel diria "5 de 5".
  // So enxergamos os ja carregados; os das paginas seguintes aparecem conforme o
  // usuario rola. O erro tem direcao segura — comeca alto e cai ao descobrir os
  // demais, nunca o contrario.
  const naoSelecionaveisCarregados = suportaSelectAll
    ? items.filter((it) => !podeSelecionar(it)).length
    : 0;

  // 'marcado' exige nada desmarcado E nenhum item inalcancavel entre os
  // carregados: afirmar "tudo selecionado" com um deles na tela seria mentira.
  const estadoCabecalho: 'vazio' | 'parcial' | 'marcado' =
    modoSelecao === 'todos'
      ? excludeIds.size === 0 && naoSelecionaveisCarregados === 0
        ? 'marcado'
        : 'parcial'
      : selectedItems.length === 0
        ? 'vazio'
        : 'parcial';

  const handleToggleCabecalho = () => {
    if (estadoCabecalho === 'vazio') entrarEmTodos();
    else limparSelecao();
  };

  // TETO, nao promessa (ADR-030 item 3). O total vem da LISTAGEM, que mostra
  // mais do que o resolver alcanca: no ramo 'pendente' ela inclui ABERTA, e o
  // resolver ainda exige saldo > 0. A mensagem de fim nunca repete este numero.
  const qtdSelecionada = contarSelecionados(
    modoSelecao,
    totalItems,
    modoSelecao === 'enumerado'
      ? selectedItems.length
      : excludeIds.size + naoSelecionaveisCarregados,
  );

  const temSelecao =
    suportaSelectAll && modoSelecao === 'todos'
      ? qtdSelecionada > 0
      : selectedItems.length > 0;

  // Devolucao no modo complemento: e o que troca o endpoint, esconde o motivo
  // por item e exige o motivo unico do lote.
  const devolverEmLote = ehModoDevolver && modoSelecao === 'todos';

  // Recarrega a lista quando um lote DESTE pai termina, se o painel ainda
  // estiver aberto. O handler vive num ref para a inscricao nao ser refeita a
  // cada render; assim o efeito depende so da chave, estavel enquanto a
  // requisicao for a mesma.
  const aoConcluirLoteRef = useRef<() => void>(() => {});
  aoConcluirLoteRef.current = () => {
    // Defesa em profundidade: o modo 'devolver' nao pertence a acao REVERTER, e
    // limpar o estado dele apagaria quantidades e motivos que o usuario digitou.
    // Hoje inalcancavel (a inscricao so existe quando suportaReversao), mas o
    // handler nao pode depender disso para estar correto.
    if (modo === 'devolver') return;
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
  };

  useEffect(() => {
    if (!suportaReversao) return;
    return aoConcluir(chaveTravaReversao, () => aoConcluirLoteRef.current());
  }, [aoConcluir, chaveTravaReversao, suportaReversao]);

  // Mesma mecanica para a devolucao (superficie 7), com handler proprio: as duas
  // acoes tem chaves distintas e mounts distintos, entao nunca disputam o mesmo
  // estado. O guard simetrico ao de cima impede que uma conclusao de DEVOLVER
  // apague o estado de um modo de reversao, caso os mounts venham a se cruzar.
  const aoConcluirDevolucaoRef = useRef<() => void>(() => {});
  aoConcluirDevolucaoRef.current = () => {
    if (ehModoReversao) return;
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
  };

  useEffect(() => {
    if (!suportaDevolucao) return;
    return aoConcluir(chaveTravaDevolucao, () => aoConcluirDevolucaoRef.current());
  }, [aoConcluir, chaveTravaDevolucao, suportaDevolucao]);

  // Os tres handlers de entrada de modo limpam estado, e limpam pelo mesmo
  // motivo: no 'view' nao ha checkbox, entao nao ha selecao legitima a herdar.
  // Aqui a chamada e no-op hoje (a selecao ja chega vazia) — ela existe para
  // que a simetria seja explicita. Ate a issue #33 este era o unico que
  // PRESERVAVA, e era por essa porta que a selecao parcial do modo 'view'
  // entrava no lote fingindo ser "todos".
  const handleEnterDevolver = () => {
    clearModeState();
    setModo('devolver');
  };

  const handleEnterDesaprovar = () => {
    clearModeState();
    setModo('desaprovar');
  };

  const handleEnterRevogar = () => {
    clearModeState();
    setModo('revogar');
  };

  const handleGiveUp = () => {
    clearModeState();
    setModo('view');
  };

  const handleSave = async () => {
    if (selectedItems.length === 0) return;

    const missingReason = selectedItems.some(item => !returnReasons[item.id] || returnReasons[item.id].trim() === '');
    if (missingReason) {
      await showAlert('Por favor, informe um motivo para todos os itens.', { variant: 'warning' });
      return;
    }

    const payload = selectedItems.map(item => ({
      id: item.id,
      qtdDevolvida: returnQuantities[item.id] ?? quantidadeDevolverPadrao(item),
      descricao: returnReasons[item.id].trim(),
    }));

    try {
      await callApi('put', '/v1/requisicoes/itens/atualizar-devolucao', payload, {
        onSuccess: (data) => {
          const updatedItems: RequisicaoItem[] = data;

          Toast.success('Atualização realizada com sucesso!');

          const updatedItemsMap = new Map(updatedItems.map(item => [item.id, item]));
          updateItems((prevItems: RequisicaoItem[]) => prevItems.map((item: RequisicaoItem) => updatedItemsMap.get(item.id) || item));

          const finalizedItemIds = updatedItems
            .filter(item => item.status === 'FINALIZADO')
            .map(item => item.id);
          setFinalizedItems(prev => [...new Set([...prev, ...finalizedItemIds])]);

          clearModeState();
          setModo('view');
          onDataChange?.();
        },
      });
    } catch (err) {
      logger.error('Erro ao atualizar itens:', err);
      await showAlert('Erro ao atualizar os itens. Tente novamente.', { variant: 'error' });
    }
    // Removido try/finally: gerenciado pelo hook.
  };

  // Devolucao ao requisitante no modo 'todos' (issue #31, superficie 7), por
  // POST /v1/requisicoes/itens/devolver.
  //
  // O modo ENUMERADO continua no PUT /v1/requisicoes/itens/atualizar-devolucao
  // (handleSave, acima), byte-identico — premissa #9. A rota nova existe porque
  // o PUT recebe um ARRAY CRU na raiz e nao ha onde pendurar selectAll sem
  // envelopar, o que quebraria os consumidores em producao.
  //
  // O que o servidor aplica ao conjunto resolvido: qtdDevolvida = saldo da
  // requisicao de cada item, e descricaoLote como motivo — e por cima disso os
  // overrides das linhas que o usuario editou.

  // Fim do caminho INLINE da devolucao. Sem contadores, ao contrario da
  // reversao: a devolucao e TUDO-OU-NADA (um item invalido reverte o lote), e o
  // corpo do 200 traz os itens devolvidos, nao uma decisao por item. Nao existe
  // aqui o soft-fail que faz contadoresInline() ter sentido na reversao.
  //
  // A lista e RELIDA inteira em vez de receber delta por id (o que o caminho
  // enumerado faz): nao ha delta possivel sobre item que nunca foi carregado.
  const finalizarDevolucaoInline = () => {
    const msg = mensagemResultado({ assincrono: false });
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
    Toast.success(msg.texto);
  };

  const dispararDevolucaoSelectAll = async () => {
    // `tipoRequisicao` alimenta `filtro.tipoRequisicao`, obrigatorio no
    // selectAll. Ele existe sempre que o modo 'devolver' e alcancavel (o botao
    // Devolver so aparece sob tipoRequisicao==='COMPRA'); o guard torna isso
    // verificavel pelo tipo em vez de deduzivel pelo fluxo.
    if (!tipoRequisicao) return;

    const descricao = descricaoLote.trim();
    if (descricao === '') {
      await showAlert('Informe o motivo da devolução para o lote.', { variant: 'warning' });
      return;
    }

    // Linhas visiveis que o usuario editou. O override exige a quantidade junto
    // (400 sem ela), entao editar so o motivo de uma linha nao tem como ser
    // expresso — e por isso a coluna Motivo sai neste modo e o motivo e unico
    // (ADR-030 ja registra essa limitacao do contrato).
    const overrides = items
      .filter(
        (it) =>
          podeSelecionar(it) &&
          !excludeIds.has(it.id) &&
          returnQuantities[it.id] !== undefined,
      )
      .map((it) => ({
        id: it.id,
        qtdDevolvida: returnQuantities[it.id],
        descricao,
      }));

    if (overrides.some((o) => o.qtdDevolvida <= 0)) {
      await showAlert('Quantidade a devolver deve ser maior que zero.', { variant: 'warning' });
      return;
    }

    const payload = {
      ...fragmentoSelectAll<
        { requisicaoId: number; tipoRequisicao: string[]; status: string[] },
        number
      >({
        filtro: {
          requisicaoId: requisicao.id,
          tipoRequisicao: [tipoRequisicao],
          status: STATUS_DEVOLVIVEIS,
        },
        excludeIds: [...excludeIds],
      }),
      descricaoLote: descricao,
      ...(overrides.length > 0 ? { overrides } : {}),
    };

    try {
      const resultado = await disparar({
        chaveTrava: chaveTravaDevolucao,
        executar: () => apiPost('/v1/requisicoes/itens/devolver', payload),
        onInline: finalizarDevolucaoInline,
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe uma devolução em andamento para esta requisição.', {
          variant: 'warning',
        });
        return;
      }

      if (resultado === 'aceito') {
        clearModeState();
        setModo('view');
        onDataChange?.();
      }
    } catch (err) {
      logger.error('Erro ao devolver itens em lote:', err);
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao devolver os itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  // Reverter alcada (desaprovar PENDENTE ou revogar REPROVADO), por
  // PUT /v1/requisicoes/{id}/itens/aprovar/reverter. Dois modos de selecao:
  //
  //   enumerado — body {requisicaoItemIds}. Resposta item a item: os que vem com
  //               revertido=true saem da lista, as falhas permanecem e tem o
  //               motivo agregado em showAlert. Caminho historico, INALTERADO.
  //   todos     — body selectAll + filtro + excludeIds; quem resolve o conjunto
  //               e o servidor, que pode responder 202 (issue #31).

  // Fim do caminho INLINE do modo complemento. O assincrono nao passa por aqui:
  // quem avisa e o OperacaoLoteProvider, porque o usuario pode ja ter saido.
  //
  // A lista e RELIDA inteira em vez de receber delta por id (o que o caminho
  // enumerado faz logo abaixo): nao ha delta possivel sobre item que nunca foi
  // carregado. reset() apos sucesso de mutacao e o padrao do projeto (premissa #4).
  const finalizarInline = (data: unknown) => {
    const decisoes = (data ?? []) as ReverterAprovacaoItemResponse[];
    const msg = mensagemResultado({
      assincrono: false,
      ...contadoresInline(decisoes, (d) => d.revertido === true),
    });
    clearModeState();
    setModo('view');
    onDataChange?.();
    reset();
    if (msg.variante === 'success') {
      Toast.success(msg.texto);
      return;
    }
    // Houve item ignorado: modal com OK, mesmo tratamento que o provider da ao
    // 202. A informacao e a mesma e nao pode sumir sozinha.
    void showAlert(msg.detalhe ? `${msg.texto}\n${msg.detalhe}` : msg.texto, {
      variant: msg.variante,
      title: 'Processamento concluído',
    });
  };

  const dispararSelectAll = async () => {
    // itemFilter e 'pendente' (Desaprovar) ou 'finalizado' (Revogar) — os botoes
    // que entram nesses modos so aparecem sob esses valores, e o backend aceita
    // exatamente esses dois numa whitelist. O mesmo valor que a listagem enviou.
    if (itemFilter !== 'pendente' && itemFilter !== 'finalizado') return;
    const payload = fragmentoSelectAll<{ itens: string }, number>({
      filtro: { itens: itemFilter },
      excludeIds: [...excludeIds],
    });
    try {
      const resultado = await disparar({
        chaveTrava: chaveTravaReversao,
        executar: () =>
          apiPut(`/v1/requisicoes/${requisicao.id}/itens/aprovar/reverter`, payload),
        onInline: finalizarInline,
      });

      if (resultado === 'bloqueado') {
        await showAlert('Já existe um processamento em andamento para esta requisição.', {
          variant: 'warning',
        });
        return;
      }

      // Aceito para segundo plano: o provider ja avisou e vai avisar de novo no
      // fim, onde quer que o usuario esteja. A lista NAO e relida agora — quem
      // recarrega e a inscricao em aoConcluir, se o painel ainda estiver aberto.
      if (resultado === 'aceito') {
        clearModeState();
        setModo('view');
        onDataChange?.();
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao processar os itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  const handleSaveReverter = async () => {
    if (modoSelecao === 'todos') {
      await dispararSelectAll();
      return;
    }
    // podeSelecionar ja desabilita selecao de item sem decisao previa; o filtro
    // aqui e defesa para o caso de selectedItems carregar residuo state-stale.
    // NAO reaproveitar no modo 'todos': la quem filtra o conjunto e o servidor.
    const elegiveis = selectedItems.filter(
      (it) => (it.qtdAprovada ?? 0) > 0 || (it.qtdReprovada ?? 0) > 0
    );
    if (elegiveis.length === 0) return;
    const payload = {
      requisicaoItemIds: elegiveis.map((it) => it.id),
    };
    try {
      await callApi(
        'put',
        `/v1/requisicoes/${requisicao.id}/itens/aprovar/reverter`,
        payload,
        {
          onSuccess: async (data: unknown) => {
            const responses = (data ?? []) as ReverterAprovacaoItemResponse[];
            const removedIds = new Set(
              responses.filter((r) => r.revertido).map((r) => r.requisicaoItemId)
            );
            // Snapshot ANTES da remocao para decidir auto-close.
            const willBeEmpty = items.filter((it) => !removedIds.has(it.id)).length === 0;

            if (removedIds.size > 0) {
              updateItems((prev: RequisicaoItem[]) =>
                prev.filter((it) => !removedIds.has(it.id))
              );
              setTotalItems((t) => Math.max(0, t - removedIds.size));
            }

            clearModeState();
            setModo('view');
            onDataChange?.();

            const sucessos = responses.filter((r) => r.revertido);
            const falhas = responses.filter((r) => !r.revertido);
            if (sucessos.length > 0) {
              Toast.success(`${sucessos.length} item(ns) revertido(s).`);
            }
            if (falhas.length > 0) {
              const motivos = falhas
                .map((r) => `• Item #${r.requisicaoItemId}: ${r.motivo ?? 'motivo nao informado'}`)
                .join('\n');
              await showAlert(`Falhas:\n${motivos}`, {
                variant: 'warning',
                title: 'Reversao parcial',
              });
            }

            if (willBeEmpty) onClose();
          },
        }
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao reverter itens. Tente novamente.', { variant: 'error' });
      }
    }
  };

  // Canal de selecao INTERNA da tabela. Desde a superficie 2 ele so dispara nos
  // modos 'view' e 'devolver': nos de reversao a prop selecaoEntrePaginas assume
  // e a tabela para de chamar este handler.
  const handleSelectChange = (newSelectedItems: RequisicaoItem[]) => {
    setSelectedItems(newSelectedItems);
  };

  const normalizeQuantityInput = (rawValue: string) => {
    const normalized = rawValue.replace(',', '.');
    if (!normalized || normalized === '.') return 0;
    const [integerPartRaw, decimalPartRaw = ''] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const decimalPart = decimalPartRaw.slice(0, 3);
    const sanitized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const parsed = Number.parseFloat(sanitized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Quantidade que o campo do modo devolver assume quando o usuario nao digita
  // nada. O numero difere por MODO DE SELECAO porque o default do SERVIDOR
  // difere, e a tela tem de mostrar o que sera gravado:
  //
  //   enumerado — min(1, saldo): o default historico do PUT legado, intocado;
  //   todos     — o saldo da requisicao, que e o default que o resolver aplica a
  //               cada item do conjunto ("selecionar tudo = devolver todo o
  //               saldo de cada", Swagger do POST /itens/devolver).
  //
  // Mostrar 1 no modo 'todos' faria a tela prometer um numero que o banco nao
  // receberia: numa linha com saldo 10, o servidor devolveria 10. O passo 8 do
  // guia admite a diferenca entre modos desde que justificada por escrito — e a
  // justificativa e esta: o default e decisao do backend, e o dado gravado tem
  // de ser o dado que o usuario viu.
  //
  // Fonte UNICA de proposito: este valor alimenta o `value` do input E o
  // fallback do payload enumerado. Repetida, a expressao permitiria que uma
  // linha nao tocada MOSTRE um numero e ENVIE outro.
  const quantidadeDevolverPadrao = (item: RequisicaoItem): number =>
    modoSelecao === 'todos'
      ? item.qtdSaldoRequisicao
      : Math.min(1, item.qtdSaldoRequisicao);

  const handleQuantityChange = (itemId: number, value: string) => {
    const maxQuantity = items.find((item: RequisicaoItem) => item.id === itemId)?.qtdSaldoRequisicao || 0;
    let numValue = normalizeQuantityInput(value);
    if (numValue > 0 && numValue < 0.001) numValue = 0.001;
    if (numValue > maxQuantity) numValue = maxQuantity;
    numValue = Number(numValue.toFixed(3));
    setReturnQuantities((prev) => ({
      ...prev,
      [itemId]: numValue,
    }));
  };

  const handleReasonChange = (itemId: number, value: string) => {
    setReturnReasons((prev) => ({
      ...prev,
      [itemId]: value.slice(0, 25),
    }));
  };

  // Exibe Qtd. Aprovada / Qtd. Reprovada apenas quando algum item da lista
  // tem decisao de alcada (> 0). Mantem a tabela enxuta em requisicoes sem
  // historico de aprovacao (ex.: itens auto-aprovados via regra granular).
  const hasAprovacaoDecisao = items.some(
    (it) => (it.qtdAprovada ?? 0) > 0 || (it.qtdReprovada ?? 0) > 0
  );
  const hasItemReversivel = items.some(
    (it) =>
      (it.status === 'REPROVADO' || it.status === 'FINALIZADO') &&
      (it.qtdReprovada ?? 0) > 0
  );
  const isTipoCompraRequisicao = tipoRequisicao === 'COMPRA';
  const hasCompraAprovada = items.some((it) => (it.qtdAprovada ?? 0) > 0);

  const aprovacaoColumns = !isTipoCompraRequisicao && hasAprovacaoDecisao
    ? [
        {
          header: 'Qtd. Aprovada',
          render: (item: RequisicaoItem) => item.qtdAprovada ?? 0,
        },
        {
          header: 'Qtd. Reprovada',
          render: (item: RequisicaoItem) => item.qtdReprovada ?? 0,
        },
      ]
    : [];

  const compraAprovacaoColumns = isTipoCompraRequisicao && hasCompraAprovada
    ? [
        {
          header: 'Qtd. Aprovada',
          render: (item: RequisicaoItem) => item.qtdAprovada ?? 0,
        },
      ]
    : [];

  const historicoCompraColumns = isTipoCompraRequisicao
    ? [
        {
          header: 'Custo Unit. Compra',
          render: (item: RequisicaoItem) => formatBRL(item.custoUnitarioUltimaCompra),
        },
        {
          header: 'Última Compra',
          render: (item: RequisicaoItem) => formatDateOrDash(item.dataUltimaCompra),
        },
      ]
    : [];

  // Exibe coluna Observação apenas quando ao menos um item tem texto.
  const hasObsItem = items.some(
    (it) => typeof it.obsItem === 'string' && it.obsItem.trim() !== ''
  );
  const observacaoColumns = hasObsItem
    ? [{ header: 'Observação', render: (item: RequisicaoItem) => item.obsItem ?? '' }]
    : [];

  const columns = [
    {
      header: 'Código',
      render: (item: RequisicaoItem) => item.codProduto,
    },
    {
      header: 'Cód. Categoria',
      render: (item: RequisicaoItem) => item.codCategoriaProduto,
    },
    {
      header: 'Nome do Produto',
      render: (item: RequisicaoItem) => item.nomeProduto,
    },
    {
      header: 'Categoria',
      render: (item: RequisicaoItem) => item.nomeCategoria,
    },
    {
      header: 'Qtd. Solicitada',
      render: (item: RequisicaoItem) => item.qtdSolicitada,
    },
    ...compraAprovacaoColumns,
    ...aprovacaoColumns,
    {
      header: 'Qtd. Devolvida',
      render: (item: RequisicaoItem) => item.qtdDevolvida,
    },
    {
      header: 'Qtd. Pendente',
      render: (item: RequisicaoItem) => item.qtdSaldoRequisicao,
    },
    {
      header: 'Qtd. Recebida',
      render: (item: RequisicaoItem) => item.qtdRecebida,
    },
    ...historicoCompraColumns,
    {
      header: 'Tipo',
      render: (item: RequisicaoItem) => item.tipoRequisicao,
    },
    {
      header: 'Situação',
      render: (item: RequisicaoItem) => item.status,
    },
    {
      header: 'Prioridade',
      render: (item: RequisicaoItem) => {
        const prioridade = item.prioridade || 'Não definida';
        if (prioridade === 'URGENTE') {
          return <span className="urgent-priority">{prioridade}</span>;
        }
        return prioridade;
      },
    },
    ...observacaoColumns,
  ];

  const returnColumns = ehModoDevolver
    ? [
        {
          header: 'Quantidade',
          render: (item: RequisicaoItem) => (
            <input
              type="number"
              min={0.001}
              max={item.qtdSaldoRequisicao}
              step={0.001}
              value={returnQuantities[item.id] ?? quantidadeDevolverPadrao(item)}
              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
              className="quantity-input"
              disabled={finalizedItems.includes(item.id)}
            />
          ),
          width: '100px',
        },
        // Motivo POR ITEM so no modo enumerado. No modo 'todos' o motivo e
        // unico (descricaoLote, no rodape): o override exige a quantidade junto
        // do motivo, entao "so o motivo desta linha" nao e expressavel no
        // contrato — limitacao ja registrada na ADR-030. Manter a coluna aqui
        // repetiria o defeito 3 da superficie 1, em que o texto digitado sumia
        // da tela E do payload ao promover a selecao.
        ...(devolverEmLote
          ? []
          : [
              {
                header: 'Motivo',
                render: (item: RequisicaoItem) => (
                  <input
                    type="text"
                    value={returnReasons[item.id] || ''}
                    onChange={(e) => handleReasonChange(item.id, e.target.value)}
                    maxLength={25}
                    placeholder="Até 25 caracteres"
                    className="reason-select"
                    disabled={finalizedItems.includes(item.id)}
                  />
                ),
                width: '160px',
              },
            ]),
      ]
    : [];

  return (
    <div className={`contact-items-modal-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isLoading || isSaving} />
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>
              Contato {header?.nroContato} | Requisição {requisicao.id} | Criada em {header ? formatDateTime(header.dataCriacao) : 'Carregando...'} {header?.dataEncerramento ? `| Encerrada em ${formatDateTime(header.dataEncerramento)}` : ''} | Situação da requisição: {header?.status}
            </h3>
          </div>
          <div className="facelet-p">
            <p>
              Origem: {header?.origem === 'PLANEJAMENTO' ? 'Planejamento' : header?.origem === 'BALCAO' ? 'Balcão' : 'Erro ao consultar origem'} | Loja: {header?.nomeFantasia} | Requisitante:{' '} {header?.nomeUsuario || 'Erro de Integração'}
            </p>
            <p>
              Código do Cliente: {header?.codCliente} | Nome do Cliente: {header?.nomeCliente || 'Erro de Integração'} | Código do consultor: {header?.codConsultor} | Nome do consultor: {header?.nomeConsultor}
            </p>
          </div>
        </div>
        <div>
          <h4>.</h4>
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <p>Carregando itens...</p>
      ) : (
        <>
          <ScrollableTable<RequisicaoItem>
            data={items}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={isLoading}
            columns={[...columns, ...returnColumns]}
            onSelectChange={handleSelectChange}
            renderHistoryAction={(item: RequisicaoItem, index: number) => (
              <button
                className="action-button"
                ref={(el) => {
                  buttonRefs.current[index] = el;
                }}
                onClick={() => handleHistoricoClick(item, index)}
                title="Ver Histórico do Item"
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </button>
            )}
            renderPurchaseHistoryAction={
              showPurchaseHistoryAction
                ? (item: RequisicaoItem) => (
                    <button
                      className="action-button"
                      onClick={() => handleCompraHistoricoClick(item)}
                      title="Ver Histórico de Compras"
                    >
                      <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </button>
                  )
                : undefined
            }
            fetchDetails={async (item: RequisicaoItem) => {
              if (!item.tipoRequisicao) {
                return [];
              }
              const res = await apiGet(`/v1/historico-itens/${item.id}/po/requisicao-item/detalhes?tipoRequisicao=${item.tipoRequisicao}`);
              return res.data.content;
            }}
            detailsColumns={[
              { header: 'Cotação', accessor: 'preOrdem' },
              { header: 'Ordem de Compra', accessor: 'ordemCompra' },
              { header: 'Qtd. Compra', accessor: 'qtdCompraOriginal' },
              { header: 'Qtd. Recebida', accessor: 'qtdRecebida' },
              { header: 'Status Recebimento', accessor: 'statusRecebimento' },
              { header: 'Comprador', accessor: 'comprador' },
              { header: 'Fornecedor', accessor: 'nomeFornecedor' },
              { header: 'Previsão Chegada', accessor: 'previsaoChegada', formatter: formatDate },
              { header: 'Conferência', accessor: 'recebimentoConferencia' },
              { header: 'Recebimento', accessor: 'dataConferencia', formatter: formatDateTime },
              { header: 'Conferente', accessor: 'conferente' },
              { header: 'Nº Nota Fiscal', accessor: 'nroNotaFiscal' },
            ]}
                emptyDetailsMessage={(item: RequisicaoItem) => getEmptyDetailsMessage(item.tipoRequisicao)} // Modificado: usa função centralizada de utils para lógica de mensagem vazia, evitando duplicação em componentes
                emptyMessage="Nenhum item encontrado para esta requisição."
                // INVARIANTE (issue #33): a coluna de checkbox so aparece onde
                // `selecaoEntrePaginas` esta ligado. As duas condicoes abaixo
                // descrevem o MESMO conjunto de modos, e mexer numa sem a outra
                // reabre o defeito: um cabecalho que marca so o carregado e se
                // apresenta como "todos". Travado em checkboxSelectAll.spec.ts.
                showCheckboxes={modo !== 'view'}
                selectionResetKey={selectionResetKey}
                renderCheckbox={podeSelecionar}
                showTotalItems={true}
                totalItems={totalItems}
                // Opt-in nos modos que tem acao em lote: os dois de reversao
                // (superficie 2) e 'devolver' (superficie 7).
                selecaoEntrePaginas={
                  suportaSelectAll
                    ? {
                        estadoCabecalho,
                        estaSelecionado: (it: RequisicaoItem) =>
                          modoSelecao === 'todos'
                            ? !excludeIds.has(it.id)
                            : selectedItems.some((s) => s.id === it.id),
                        onToggleCabecalho: handleToggleCabecalho,
                        onSelecionarItem: handleSelecionarItem,
                      }
                    : undefined
                }
              />
          <div className="form-actions">
            {modo === 'view' && (
              <>
                {tipoRequisicao === 'COMPRA' && (
                  <ReturnButton onClick={handleEnterDevolver} disabled={isLoading || isSaving} />
                )}
                {podeAprovar && itemFilter === 'pendente' && hasAprovacaoDecisao && (
                  <RejectButton onClick={handleEnterDesaprovar} disabled={isLoading || isSaving} />
                )}
                {podeAprovar && itemFilter === 'finalizado' && hasAprovacaoDecisao && hasItemReversivel && (
                  <button
                    type="button"
                    className="save-button"
                    onClick={handleEnterRevogar}
                    disabled={isLoading || isSaving}
                    title="Revogar reprovacao"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Aprovar
                  </button>
                )}
              </>
            )}
            {modo !== 'view' && (
              <>
                {/* Escopo da selecao, nos modos que tem acao em lote.
                    Informativo — o gesto e o checkbox do cabecalho. */}
                {suportaSelectAll && estadoCabecalho !== 'vazio' && (
                  <span className="reversao-info-selecao">
                    {textoSelecao(qtdSelecionada, totalItems)}
                  </span>
                )}
                {/* Motivo UNICO do lote na devolucao. Obrigatorio: viaja ate o
                    e-mail que o requisitante recebe, entao nao ha default
                    vazio. Aparece so no modo 'todos', que e quando a coluna
                    Motivo por item sai da tabela. */}
                {devolverEmLote && (
                  <span className="motivo-lote-devolucao">
                    <label htmlFor="descricao-lote-devolucao">Motivo da devolução:</label>
                    <input
                      id="descricao-lote-devolucao"
                      type="text"
                      value={descricaoLote}
                      onChange={(e) => setDescricaoLote(e.target.value.slice(0, DESCRICAO_LOTE_MAX))}
                      maxLength={DESCRICAO_LOTE_MAX}
                      placeholder={`Até ${DESCRICAO_LOTE_MAX} caracteres`}
                      className="motivo-lote-input"
                      disabled={isSaving || processandoLote}
                    />
                  </span>
                )}
                <GiveUpButton
                  onClick={handleGiveUp}
                  disabled={isLoading || isSaving || processandoLote}
                />
                {temSelecao && (
                  <SaveButton
                    isLoading={isSaving || processandoLote}
                    onClick={
                      ehModoDevolver
                        ? devolverEmLote
                          ? dispararDevolucaoSelectAll
                          : handleSave
                        : handleSaveReverter
                    }
                    disabled={isLoading || isSaving || processandoLote}
                    label={
                      modo === 'desaprovar'
                        ? 'Desaprovar'
                        : modo === 'revogar'
                          ? 'Revogar'
                          : undefined
                    }
                  />
                )}
              </>
            )}
          </div>
          <Modal
            isOpen={historicoModalOpen}
            onClose={handleCloseHistoricoModal}
            theme={theme}
            zIndex={2000}
          >
            {historicoData && selectedItemId && (
              <HistoricoItemModalContent
                historico={historicoData}
                requisicaoItemId={selectedItemId}
                isOpen={historicoModalOpen}
                onClose={handleCloseHistoricoModal}
                theme={theme}
                showCancelButton={false}
              />
            )}
          </Modal>
          {showPurchaseHistoryAction && (
            <Modal
              isOpen={compraHistoricoModalOpen}
              onClose={handleCloseCompraHistoricoModal}
              theme={theme}
              zIndex={2000}
            >
              {compraHistoricoData && (
                <CompraHistoricoModalContent
                  compraHistorico={compraHistoricoData}
                  isOpen={compraHistoricoModalOpen}
                  onClose={handleCloseCompraHistoricoModal}
                  theme={theme}
                  showCancelButton={false}
                />
              )}
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default RequisicaoItemsSidePanelContent;
